import React, {useEffect} from "react";
import {shallowEqual} from "react-redux";
import {Centre} from "../Centre";

import "./ItemView.scss";
import {AccountItemView} from '../accounts/AccountItemView';
import {
    isAccountContent,
    isCalendarEntryContent,
    isContactContent,
    isDummyContent,
    isEmailAnalysisContent,
    isEmailContent,
    isEmailRuleContent,
    isEmailSourceContent,
    isLoginRequiredContent,
    isPgpKeyContent,
    isRawEmailContent,
    isUnknownPgpKeyContent,
    isUserDtoContent,
    ItemContent
} from '../../domain/ItemContent';
import AnalyseEmailView from '../analysis/AnalyseEmailView';
import {EmailSourceView} from '../source/EmailSourceView';
import {ContactContentView} from "../contacts/ContactContentView";
import {CalendarContentView} from "../calendar/CalendarContentView";
import {PgpKeyItemView} from "../pgpkeys/PgpKeyItemView";
import {UnknownPgpKeyItemView} from "../pgpkeys/UnknownPgpKeyItemView";
import {RuleItemView} from "../rules/RuleItemView";
import LoginView from "../login/LoginView";
import {EmailThreadView} from "../email/EmailThreadView";
import {RawEmailView} from "../email/RawEmailView";
import {markSelectedEmailAsRead} from "../../locations/EmailLocation";
import {useAppDispatch, useAppSelector} from "../../hooks/ReduxHooks";
import {UserItemView} from "../users/UserItemView";

interface RenderItemContentProps {
    itemContent: ItemContent;
}

const RenderItemContent = ({itemContent}: RenderItemContentProps) => {
    if (isDummyContent(itemContent)) {
        if (itemContent.dummyType === "none") {
            return (<Centre>No item selected</Centre>);
        }
        if (itemContent.dummyType === "loading") {
            return (<Centre/>);
        }
        return <div>Unsupported dummy type {itemContent.dummyType}</div>;
    }

    if (isEmailAnalysisContent(itemContent)) {
        return <AnalyseEmailView analysis={itemContent}/>;
    }
    if (isRawEmailContent(itemContent)) {
        return <RawEmailView content={itemContent.Source} embeddedView={false}/>;
    }
    if (isEmailSourceContent(itemContent)) {
        return <EmailSourceView emailSource={itemContent}/>;
    }
    if (isEmailContent(itemContent)) {
        return <EmailThreadView itemContent={itemContent}/>;
    }
    if (isAccountContent(itemContent)) {
        return <AccountItemView account={itemContent}/>;
    }
    if (isContactContent(itemContent)) {
        return <ContactContentView contact={itemContent}/>;
    }
    if (isCalendarEntryContent(itemContent)) {
        return <CalendarContentView calendarEntry={itemContent}/>;
    }
    if (isPgpKeyContent(itemContent)) {
        return <PgpKeyItemView pgpKey={itemContent}/>;
    }
    if (isUnknownPgpKeyContent(itemContent)) {
        return <UnknownPgpKeyItemView pgpKey={itemContent}/>;
    }
    if (isEmailRuleContent(itemContent)) {
        return <RuleItemView rule={itemContent}/>;
    }
    if (isLoginRequiredContent(itemContent)) {
        return <LoginView/>;
    }
    if (isUserDtoContent(itemContent)) {
        return <UserItemView selectedUser={itemContent}/>;
    }
    return (
        <Centre>
            <>Unsupported item type: {typeof itemContent}</>
        </Centre>
    );
};

const ItemView = ({itemContentIndex}: { itemContentIndex: 0 | 1 }) => {

    const oldItemContent = useAppSelector(s => s.itemState.itemContents[(itemContentIndex + 1) % 2], shallowEqual);
    const itemContent = useAppSelector(s => s.itemState.itemContents[itemContentIndex], shallowEqual);
    const isCurrentContent = useAppSelector(s => itemContentIndex === s.itemState.itemContentIndex);

    const isLoadingContent = isDummyContent(itemContent) && itemContent.dummyType === "loading";

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!isLoadingContent && isCurrentContent) {
            // console.log("Content Change", oldItemContent, " -> ", itemContent);
            if (isEmailContent(oldItemContent)
                && isEmailContent(itemContent)
                && oldItemContent.Id !== itemContent.Id
                && oldItemContent.Id > -1
            ) {
                // We've changed the email that's being viewed - mark the previously displayed one as read (only applies to desktop)
                // TODO: support marking all emails in the thread as read
                markSelectedEmailAsRead(dispatch, oldItemContent.Emails.filter(e => e.Id === oldItemContent.Id));
            }
        }
    }, [dispatch, isLoadingContent, oldItemContent, itemContent, isCurrentContent]);

    return <RenderItemContent itemContent={itemContent}/>;
}

export default ItemView;
